import { ApplicationData } from "../ApplicationData";
import { FormatDate } from "../FormatDate";

/**
 * @typedef {object} TimeData
 * @property {number} hour - La hora (0-23).
 * @property {number} minute - El minuto (0-59).
 */

/**
 * @typedef {object} ExceptionData
 * @property {"date"|"weekday"} type - El tipo de excepción ("date" para una fecha específica, "weekday" para un día de la semana).
 * @property {string} [date] - La fecha de la excepción en formato ISO 8601 (ej. "YYYY-MM-DD"). Obligatorio si `type` es "date".
 * @property {number} [weekday] - El día de la semana (0 para Domingo, 6 para Sábado). Obligatorio si `type` es "weekday".
 * @property {"inclusive"|"exclusive"} exception_type - Si la excepción habilita (`inclusive`) o deshabilita (`exclusive`) el soporte.
 * @property {number} start_time_hour - La hora de inicio de la excepción (0-23).
 * @property {number} start_time_minute - El minuto de inicio de la excepción (0-59).
 * @property {number} end_time_hour - La hora de fin de la excepción (0-23).
 * @property {number} end_time_minute - El minuto de fin de la excepción (0-59).
 */

/**
 * @class support_customer_service
 * @description
 * Clase para gestionar y mostrar el horario de atención al cliente,
 * con soporte para conversiones entre zonas horarias y manejo de excepciones.
 * Permite definir un horario base en una zona horaria de "soporte" (ej. oficina central)
 * y mostrarlo o evaluarlo en la zona horaria del usuario final.
 */
class support_customer_service {

    /**
     * Crea una instancia de support_customer_service.
     * @param {object} [data={}] - Objeto de configuración inicial para el horario de soporte.
     * @param {number} [data.start_day=1] - El día de la semana en que comienza el horario regular de soporte (0=Domingo, 6=Sábado). Por defecto es 1 (Lunes).
     * @param {number} [data.end_day=5] - El día de la semana en que termina el horario regular de soporte. Por defecto es 5 (Viernes).
     * @param {TimeData} [data.start_time={hour: 9, minute: 0}] - La hora de inicio del soporte en la `supportTimeZone`.
     * @param {TimeData} [data.end_time={hour: 19, minute: 30}] - La hora de fin del soporte en la `supportTimeZone`.
     * @param {Array<ExceptionData>} [data.exceptions=[]] - Un array de objetos que definen horarios excepcionales o feriados.
     * @param {string} [data.supportTimeZone='America/Bogota'] - La zona horaria donde se define el horario base de soporte.
     * Ejemplo: 'America/Bogota', 'America/New_York', 'Europe/Madrid'.
     * @param {string} [userTimeZone=Intl.DateTimeFormat().resolvedOptions().timeZone] - La zona horaria del usuario que consulta el servicio.
     * Por defecto, la zona horaria detectada por el navegador.
     * @param {function} [callback=function(){}] - Una función de callback.
     */
    constructor(data = {}, userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone, callback = function(){}) {
        /**
         * El día de la semana en que comienza el horario regular de soporte (0=Domingo, 6=Sábado).
         * @type {number}
         */
        this.start_day = data?.start_day ?? 1; // Lunes
        
        /**
         * El día de la semana en que termina el horario regular de soporte (0=Domingo, 6=Sábado).
         * @type {number}
         */
        this.end_day = data?.end_day ?? 5;     // Viernes
        
        /**
         * La hora de inicio del soporte en la `supportTimeZone`.
         * @type {TimeData}
         */
        this.start_time = {
            "hour": data?.start_time?.hour ?? 9,
            "minute": data?.start_time?.minute ?? 0
        };
        
        /**
         * La hora de fin del soporte en la `supportTimeZone`.
         * @type {TimeData}
         */
        this.end_time = {
            "hour": data?.end_time?.hour ?? 19,
            "minute": data?.end_time?.minute ?? 30
        };
        
        /**
         * Array de objetos que definen horarios excepcionales o feriados.
         * @type {Array<ExceptionData>}
         */
        this.exceptions = data?.exceptions ?? [];
        
        /**
         * Función de callback.
         * @type {function}
         */
        this.callback = callback ?? function(){};
        
        /**
         * La zona horaria donde se define el horario base de soporte.
         * Este es el "origen" del horario.
         * @type {string}
         */
        this.supportTimeZone = data?.supportTimeZone ?? 'America/Bogota'; 
        
        /**
         * La zona horaria del usuario final que consulta el servicio.
         * El horario se adaptará a esta zona.
         * @type {string}
         */
        this.userTimeZone = userTimeZone; 
    }

    /**
     * Convierte una hora y minuto específicos desde la zona horaria de soporte
     * a una zona horaria de destino (`targetTimeZone`).
     * Esta función es crucial para adaptar el horario de soporte a la perspectiva del usuario.
     *
     * @private
     * @param {number} hour - La hora (0-23) en la zona horaria de soporte.
     * @param {number} minute - El minuto (0-59) en la zona horaria de soporte.
     * @param {Date} referenceDate - Una fecha de referencia. Se utiliza el año, mes y día de esta fecha
     * para manejar correctamente el horario de verano (DST) si aplica en ambas zonas.
     * @param {string} targetTimeZone - La zona horaria a la que se desea convertir (ej. `this.userTimeZone`).
     * @returns {{hour: number, minute: number}} Un objeto que contiene la hora y el minuto convertidos a la zona horaria objetivo.
     */
    convertSupportTimeToTargetTimeZone(hour, minute, referenceDate, targetTimeZone) {
        const app = this;
        
        // Obtener el año, mes y día de la `referenceDate` en la `supportTimeZone`.
        let supportDateParts = new Intl.DateTimeFormat('en-US', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            timeZone: app.supportTimeZone
        }).formatToParts(referenceDate);

        let year = parseInt(supportDateParts.find(p => p.type === 'year').value, 10);
        let month = parseInt(supportDateParts.find(p => p.type === 'month').value, 10); // 1-indexed for Intl.DateTimeFormat
        let day = parseInt(supportDateParts.find(p => p.type === 'day').value, 10);

        // Calcular el punto absoluto en el tiempo (UTC timestamp)        
        // Creamos una fecha UTC base para el día de referencia. Esto representa las 00:00:00 UTC de ese día.
        let baseUtcDate = new Date(Date.UTC(
            referenceDate.getFullYear(), // Importante usar el año del objeto Date original para el DST
            referenceDate.getMonth(),    // El mes para Date.UTC es 0-indexado
            referenceDate.getDate()
        ));

        // Obtenemos el offset de la `supportTimeZone` respecto a UTC para `baseUtcDate`.
        // Para esto, convertimos `baseUtcDate` (que es UTC) a una fecha localizada en `supportTimeZone`,
        // y luego calculamos la diferencia con la misma fecha en UTC.
        // Esto nos da el offset real en milisegundos para esa zona y esa fecha (considerando DST).
        let d_support = new Date(baseUtcDate.toLocaleString('en-US', { timeZone: app.supportTimeZone }));
        let d_utc = new Date(baseUtcDate.toLocaleString('en-US', { timeZone: 'UTC' }));
        let offsetMs_support = d_support.getTime() - d_utc.getTime(); // Diferencia en ms entre supportTimeZone y UTC

        // Calculamos el momento exacto en UTC (en milisegundos)
        // que corresponde a la `hour:minute` en la `supportTimeZone`.
        // Partimos de la `baseUtcDate` (00:00:00 UTC del día),
        // le sumamos la hora y minuto del soporte,
        // y luego ajustamos por el `offsetMs_support`.
        // Si la zona de soporte es UTC-5, `offsetMs_support` será negativo (-5 horas).
        // Al restar un valor negativo, estamos sumando las 5 horas para llegar al UTC correcto.
        let targetMomentInMs = baseUtcDate.getTime() + (hour * 60 + minute) * 60 * 1000 - offsetMs_support;

        // Creamos un objeto Date que representa este punto absoluto en el tiempo (UTC).
        let absolutePointInTime = new Date(targetMomentInMs);

        // Formatear ese punto absoluto en el tiempo a la `targetTimeZone`.
        // `Intl.DateTimeFormat` se encargará de convertir el timestamp UTC a la hora local
        // en la `targetTimeZone`, aplicando correctamente su propio DST si aplica.
        let parts = new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23', // Formato 24 horas para facilitar el parseo
            timeZone: targetTimeZone 
        }).formatToParts(absolutePointInTime);

        let h = parseInt(parts.find(p => p.type === 'hour').value, 10);
        let m = parseInt(parts.find(p => p.type === 'minute').value, 10);
        
        return { hour: h, minute: m };
    }

    /**
     * Determina si el botón de soporte debe estar deshabilitado en una fecha y hora dadas.
     * Considera el horario regular, la zona horaria del usuario y las excepciones.
     * @param {Date} date - La fecha y hora actual que se desea evaluar (normalmente la fecha/hora actual del usuario).
     * @returns {boolean} `true` si el soporte está deshabilitado, `false` si está habilitado.
     */
    isSupportButtonDisabled(date) {
        const app = this;
        let userCurrentParts = new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            weekday: 'short',
            hourCycle: 'h23',
            timeZone: app.userTimeZone
        }).formatToParts(date);

        let userCurrentHour = parseInt(userCurrentParts.find(p => p.type === 'hour').value, 10);
        let userCurrentMinute = parseInt(userCurrentParts.find(p => p.type === 'minute').value, 10);
        let userWeekdayStr = userCurrentParts.find(p => p.type === 'weekday').value;

        let weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        let userCurrentDay = weekdays[userWeekdayStr];
        let userCurrentTotalMinutes = userCurrentHour * 60 + userCurrentMinute;

        let convertedStartTime = app.convertSupportTimeToTargetTimeZone(app.start_time.hour, app.start_time.minute, date, app.userTimeZone);
        let convertedEndTime = app.convertSupportTimeToTargetTimeZone(app.end_time.hour, app.end_time.minute, date, app.userTimeZone);

        let startTotalMinutes = convertedStartTime.hour * 60 + convertedStartTime.minute;
        let endTotalMinutes = convertedEndTime.hour * 60 + convertedEndTime.minute;

        if (userCurrentDay < app.start_day || userCurrentDay > app.end_day && app.exceptions.length === 0) {
            return true; 
        }

        let isWithinSchedule = userCurrentTotalMinutes >= startTotalMinutes && userCurrentTotalMinutes <= endTotalMinutes;

        for (let exception of app.exceptions ?? []) {
            let applies = false;

            if (exception.type === "date") {
                const exceptionDateObj = new Date(exception.date); 
                
                let exceptionDatePartsInSupportTZ = new Intl.DateTimeFormat('en-US', {
                    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: app.supportTimeZone
                }).formatToParts(exceptionDateObj); 

                let exceptionYear = exceptionDatePartsInSupportTZ.find(p => p.type === 'year').value;
                let exceptionMonth = exceptionDatePartsInSupportTZ.find(p => p.type === 'month').value;
                let exceptionDay = exceptionDatePartsInSupportTZ.find(p => p.type === 'day').value;
                let exceptionDateStr = `${exceptionYear}-${exceptionMonth}-${exceptionDay}`;

                let currentDatePartsInSupportTZ = new Intl.DateTimeFormat('en-US', {
                    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: app.supportTimeZone
                }).formatToParts(date); 
                
                let currentYear = currentDatePartsInSupportTZ.find(p => p.type === 'year').value;
                let currentMonth = currentDatePartsInSupportTZ.find(p => p.type === 'month').value;
                let currentDay = currentDatePartsInSupportTZ.find(p => p.type === 'day').value;
                let currentDateStr = `${currentYear}-${currentMonth}-${currentDay}`;
                
                applies = (exceptionDateStr === currentDateStr);

            } else if (exception.type === "weekday") {
                applies = (userCurrentDay === exception.weekday);
            }

            if (!applies) {
                continue;
            }

            const isFullDayException = (
                exception.start_time_hour === 0 &&
                exception.start_time_minute === 0 &&
                exception.end_time_hour === 23 &&
                exception.end_time_minute === 59
            );

            let withinExceptionTime;
            if (isFullDayException) {
                withinExceptionTime = true; 
            } else {
                let convertedExceptionStart = app.convertSupportTimeToTargetTimeZone(exception.start_time_hour, exception.start_time_minute, date, app.userTimeZone);
                let convertedExceptionEnd = app.convertSupportTimeToTargetTimeZone(exception.end_time_hour, exception.end_time_minute, date, app.userTimeZone);

                let exceptionStart = convertedExceptionStart.hour * 60 + convertedExceptionStart.minute;
                let exceptionEnd = convertedExceptionEnd.hour * 60 + convertedExceptionEnd.minute;

                // Manejo de cruce de medianoche para horarios parciales si end_time es al día siguiente
                if (exceptionEnd < exceptionStart) { // La excepción cruza la medianoche del usuario
                    withinExceptionTime = (userCurrentTotalMinutes >= exceptionStart || userCurrentTotalMinutes <= exceptionEnd);
                } else {
                    withinExceptionTime = userCurrentTotalMinutes >= exceptionStart && userCurrentTotalMinutes <= exceptionEnd;
                }
            }
            
            if (withinExceptionTime) {
                if (exception.exception_type === "exclusive") {
                    return true;
                } else if (exception.exception_type === "inclusive") {
                    return false; 
                }
            }
        }

        if (userCurrentDay < app.start_day || userCurrentDay > app.end_day) {
            return true; 
        }
        
        return !isWithinSchedule;
    }

    /**
     * Genera un texto amigable que muestra el horario de atención al cliente,
     * adaptado a la zona horaria del usuario.
     * @param {Date} [date=new Date()] - La fecha de referencia para la cual se calculará el horario.
     * Por defecto, es la fecha y hora actual del sistema.
     * @returns {string} Una cadena de texto que describe el horario de atención, como "Lun-Vie 08:00 a. m. a 07:30 p. m.".
     */
    isAvailableSupportText(date = new Date()) { 
        const app = this;
        let lang = "en";
        if(typeof ApplicationData !== "undefined"){
            try {
                lang = ApplicationData.get().user.lang;
            } catch (error) {
                lang = "es";
            }
        }else{
            lang = "es";
        }
        
        
        // Nombres de los días de la semana según el idioma.
        let daysES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
        let daysEN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        let days = lang === "es" ? daysES : daysEN;

        /**
         * Función auxiliar para formatear una hora y minuto específicos
         * (que ya están en la zona horaria del USUARIO) a un formato de cadena legible.
         * @param {number} hour - La hora (0-23) a formatear.
         * @param {number} minute - El minuto (0-59) a formatear.
         * @returns {string} La hora formateada (ej. "08:00 a. m." o "09:30 PM").
         */
        let formatDisplayTime = (hour, minute) => {
            // Se crea una fecha temporal para que `Intl.DateTimeFormat` pueda aplicar
            // el formato de hora y minuto deseado, considerando la `userTimeZone`.
            let tempDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute, 0, 0);

            return new Intl.DateTimeFormat(lang === "es" ? 'es-CO' : 'en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: lang === "es", // Usar AM/PM si el idioma es español
                timeZone: app.userTimeZone // Formatear para la zona del USUARIO
            }).format(tempDate);
        };

        // Convertir las horas de inicio/fin del horario regular (definidas en `supportTimeZone`)
        // a la zona horaria del usuario (`userTimeZone`) para la visualización.
        let convertedStartTime = app.convertSupportTimeToTargetTimeZone(app.start_time.hour, app.start_time.minute, date, app.userTimeZone);
        let convertedEndTime = app.convertSupportTimeToTargetTimeZone(app.end_time.hour, app.end_time.minute, date, app.userTimeZone);

        let startDay = days[app.start_day];
        let endDay = days[app.end_day];
        let startTime = formatDisplayTime(convertedStartTime.hour, convertedStartTime.minute);
        let endTime = formatDisplayTime(convertedEndTime.hour, convertedEndTime.minute);

        let baseText = lang === "es"
            ? `${startDay}-${endDay} ${startTime} a ${endTime}`
            : `${startDay}-${endDay} ${startTime} to ${endTime}`;

        // Filtrar y procesar excepciones de tipo "weekday" que son "inclusive" (horarios adicionales).
        let weekdayExceptions = app.exceptions.filter(e => e.type === "weekday" && e.exception_type === "inclusive");

        if (weekdayExceptions.length > 0) {
            let exceptionText = weekdayExceptions.map(e => {
                let dayName = days[e.weekday];
                // Convertir las horas de la excepción (definidas en `supportTimeZone`)
                // a la zona horaria del usuario (`userTimeZone`) para la visualización.
                let convertedExceptionStart = app.convertSupportTimeToTargetTimeZone(e.start_time_hour, e.start_time_minute, date, app.userTimeZone);
                let convertedExceptionEnd = app.convertSupportTimeToTargetTimeZone(e.end_time_hour, e.end_time_minute, date, app.userTimeZone);

                let start = formatDisplayTime(convertedExceptionStart.hour, convertedExceptionStart.minute);
                let end = formatDisplayTime(convertedExceptionEnd.hour, convertedExceptionEnd.minute);

                return `${dayName} ${start} ${lang === "es" ? "a" : "to"} ${end}`;
            }).join(`, \n`); // Unir múltiples excepciones con coma y salto de línea

            baseText += `,\n ${exceptionText}`;
        }

        return baseText;
    }
}

function isSupportButtonDisabled(date) {
    // Get the current day of the week (0 for Sunday, 6 for Saturday)
    const day = date.getDay();
    
    // Get the current hour (0-23)
    const hour = date.getHours();

    // get current minutes 0-60
    const minutes = date.getMinutes();

    // Check if it's within business hours on weekdays
    if(day >= 1 && day <= 5){ 
        // Check if it's within business hours on weekdays
        if(hour >= 7 && (hour + (minutes/60) < 19.50)){
            return false; // Button enabled on weekdays between 9AM and 7:30PM EST
        }
    }
    
    // Check if it's within business hours on Saturday
    if (day === 6 && hour >= 8 && hour < 12) {
        return false; // Button enabled on Saturday between 9AM and 6PM EST
    }

    // on sunday: block.
    if(day > 6){
        return false;
    }
    
    // All other cases, button is disabled
    return true;
}

function isAvailableSupportText(){

    let content = "Lun-Vie 9:00AM-7:30PM, Sáb 8:00AM-12:00PM EST"
    if(typeof ApplicationData !== "undefined"){
        try {
            if(ApplicationData.get().user.lang !== "es"){
                content = "Mon-Fri 09:00-19:30, Sáb 08:00-12:00 EST"
            }
        } catch (error) {
            console.log(error);
        }
    }

    return content;
}



async function get_support_customer_service_data() {
    if(window.support_customer_service_data){
        return window.support_customer_service_data;
    }

    let formatDate = new FormatDate(new Date());
    let monthday = formatDate.month_day_year_only_hours();

    // if superuser cookie is present use unixtime
    try {
        if(window.Cookies.get("AppSessionSuperUser") !== null){
            monthday = new Date().getTime();
        }
    } catch (error) {
        console.log(error);
    }
    
    const IS_ONLINE = window.IS_ONLINE;
    let response = await fetch(`https://bucket.3pod.io/tools/${IS_ONLINE ? "prod": "test"}/support_service_availability.json?time=${monthday}`);
    let data = await response.json();
    window.support_customer_service_data = data;
    return data;
}


window.isSupportButtonDisabled = isSupportButtonDisabled;

export {
    get_support_customer_service_data,
    isSupportButtonDisabled,
    isAvailableSupportText,
    support_customer_service
}